import { supabase } from '@/lib/supabase';

/**
 * Trading Signals Service
 * Manages trading signals database operations
 */
class TradingSignalsService {
  /**
   * Fetch active trading signals from database
   */
  async getActiveSignals(filters?: {
    assetClass?: string;
    timeframe?: string;
    minConfidence?: number;
  }) {
    try {
      let query = supabase
        .from('trading_signals')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (filters?.assetClass && filters.assetClass !== 'ALL') {
        query = query.eq('asset_class', filters.assetClass.toLowerCase());
      }

      if (filters?.timeframe && filters.timeframe !== 'ALL') {
        query = query.eq('timeframe', filters.timeframe);
      }

      if (filters?.minConfidence) {
        query = query.gte('confidence_score', filters.minConfidence);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error: any) {
      console.error('Error fetching active signals:', error);
      throw error;
    }
  }

  /**
   * Fetch signal by ID
   */
  async getSignalById(signalId: string) {
    try {
      const { data, error } = await supabase
        .from('trading_signals')
        .select('*')
        .eq('id', signalId)
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error: any) {
      console.error('Error fetching signal:', error);
      throw error;
    }
  }

  /**
   * Create a new trading signal (admin/system only)
   */
  async createSignal(signalData: {
    asset_symbol: string;
    asset_class: string;
    direction: string;
    entry_price: number;
    target_price: number;
    stop_loss: number;
    confidence_score: number;
    timeframe: string;
    signal_name: string;
    description?: string;
    created_by?: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('trading_signals')
        .insert([signalData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error: any) {
      console.error('Error creating signal:', error);
      throw error;
    }
  }

  /**
   * Update signal status
   */
  async updateSignalStatus(signalId: string, isActive: boolean) {
    try {
      const { data, error } = await supabase
        .from('trading_signals')
        .update({ is_active: isActive })
        .eq('id', signalId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error: any) {
      console.error('Error updating signal status:', error);
      throw error;
    }
  }

  /**
   * Get signal performance metrics
   */
  async getSignalMetrics() {
    try {
      const { data: signals, error } = await supabase
        .from('trading_signals')
        .select('confidence_score, is_active')
        .eq('is_active', true);

      if (error) {
        throw error;
      }

      const totalActive = signals?.length || 0;
      const avgConfidence =
        signals?.reduce((sum, s) => sum + (s.confidence_score || 0), 0) /
          totalActive || 0;

      return {
        totalActive,
        avgConfidence: Math.round(avgConfidence),
      };
    } catch (error: any) {
      console.error('Error fetching signal metrics:', error);
      return {
        totalActive: 0,
        avgConfidence: 0,
      };
    }
  }
}

export const tradingSignalsService = new TradingSignalsService();
export default TradingSignalsService;